import type { Conditions } from '@risksmart-app/form-configuration/src/field-types/types';
import type {
  ModuleConfig,
  OrgFeature,
} from '@risksmart-app/modules/src/index';
import {
  type FormId,
  getFormConfigRegistry,
} from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormConfig } from '@risksmart-app/shared/forms/types';
import type { VertexBody } from 'digraph-js';
import { DiGraph } from 'digraph-js';
import _ from 'lodash';
import type { FieldValues } from 'react-hook-form';
import type { Helpers } from 'src/pages/custom-datasources/update/display-types/types';

import { processItems } from '../../../../../node_modules/@cloudscape-design/collection-hooks/mjs/operations';
import type { AdditionalConditionField } from '../../edit-field-modal/formRegistryService';
import { getConditionalPropertyFilterProps } from '../../edit-field-modal/formRegistryService';
import { defaultFieldConditionToConditions } from '../convertEditorStateToFormData';
import type {
  CustomisableFormDataContextState,
  TypedFormFieldConfiguration,
} from '../customisable-form-data/CustomisableFormDataContext';

export type ConditionGraph = DiGraph<{
  id: string;
  body: VertexBody;
  adjacentTo: string[];
}>;

export type ConditionalField = Pick<
  TypedFormFieldConfiguration,
  'FieldId' | 'Conditions'
>;

export type ConditionalFields = ConditionalField[];

export type ConditionalSection = {
  SectionId: string;
  Conditions?: TypedFormFieldConfiguration['Conditions'];
};

export type ConditionalSections = ConditionalSection[];

/**
 * Builds a directed graph from field conditions.
 * The function creates a graph where each node represents a field and edges represent
 * conditional dependencies between fields.
 *
 * @returns DiGraph - Graph of all fields and their dependencies
 */
export const buildFieldConditionGraph = (
  fields: ConditionalField[]
): ConditionGraph => {
  // Build adjacency list representation of the dependency graph
  const graph = new DiGraph();

  fields.forEach((field) => {
    graph.addVertices({ id: field.FieldId, adjacentTo: [], body: {} });
  });

  fields.forEach((field) => {
    (field.Conditions?.tokenGroups || []).forEach((tokenGroup) => {
      if ('tokens' in tokenGroup) {
        tokenGroup.tokens.forEach((token) => {
          graph.addEdge({ from: token.propertyKey || '', to: field.FieldId });
        });
      } else {
        graph.addEdge({
          from: tokenGroup.propertyKey || '',
          to: field.FieldId,
        });
      }
    });
  });

  return graph;
};

/**
 * Returns the passed fieldId, and all of its ancestors
 */
export const getAncestorsAndSelf = (
  fieldGraph: ConditionGraph,
  fieldId: string
): string[] => [fieldId, ...Array.from(fieldGraph.getDeepParents(fieldId))];

type ConditionFilteringProperty = ReturnType<
  typeof getConditionalPropertyFilterProps
>['filteringProperties'][number];

type ConditionOperator = NonNullable<
  ConditionFilteringProperty['operators']
>[number];

type CustomMatchOperator = Extract<ConditionOperator, object> & {
  match: (itemValue: unknown, tokenValue: unknown) => boolean;
};

const isCustomMatchOperator = (
  op: ConditionOperator
): op is CustomMatchOperator =>
  typeof op === 'object' && op !== null && typeof op.match === 'function';

/**
 * A property matches by exact identifier (not free text) when it either has
 * enumerable `filteringOptions` (custom select/multiselect attributes) or
 * defines a custom operator `match` function (standard owner/contributor/
 * department/tag fields, whose row value is a `{ value }` object/array compared
 * by strict id equality). Such keys must never be lower/trimmed — normalising
 * only the rule's token value would desync it from the untouched row value.
 */
const hasCustomMatcher = (fp: ConditionFilteringProperty): boolean =>
  (fp.operators ?? []).some(isCustomMatchOperator);

/**
 * The form editor's conditional rules always store `=` ("Is equal to") tokens,
 * but the multi-valued standard fields (owner/contributor/department/tag)
 * declare only the `:` / `!:` table-filter operators on their property config.
 * Cloudscape's filterByToken silently evaluates to false for any token whose
 * operator the property doesn't declare, so a `=` condition on those fields
 * could never fire. Alias the property's positive custom matcher onto `=`
 * (and its negation onto `!=`) at evaluation time; the matcher already
 * implements id-equality-within-the-value semantics, which is exactly what
 * the rule editor's "Is equal to" means for multi-valued fields. Stored
 * conditions and the rule-builder UIs are untouched.
 */
const withEqualityOperators = (
  fp: ConditionFilteringProperty
): ConditionFilteringProperty => {
  const operators = fp.operators ?? [];
  const positive = operators.find(
    (op): op is CustomMatchOperator =>
      isCustomMatchOperator(op) && !op.operator.startsWith('!')
  );
  if (!positive) {
    return fp;
  }

  const declared = new Set(
    operators.map((op) =>
      typeof op === 'object' && op !== null ? op.operator : op
    )
  );
  const aliases: CustomMatchOperator[] = [];
  if (!declared.has('=')) {
    aliases.push({ ...positive, operator: '=' });
  }
  if (!declared.has('!=')) {
    aliases.push({
      ...positive,
      operator: '!=',
      match: (itemValue: unknown, tokenValue: unknown): boolean =>
        !positive.match(itemValue, tokenValue),
    });
  }

  return aliases.length > 0
    ? { ...fp, operators: [...operators, ...aliases] }
    : fp;
};

/**
 * Returns the set of property keys that are free-text — i.e. matched by default
 * string equality, so case/whitespace normalisation is safe and intended.
 * Enumerable keys (custom select/multiselect via `filteringOptions`) and
 * id-matched keys (standard fields with a custom operator `match`) are excluded.
 */
const getFreeTextPropertyKeys = (
  filteringProps: ReturnType<typeof getConditionalPropertyFilterProps>
): Set<string> => {
  const keysWithOptions = new Set(
    (filteringProps.filteringOptions ?? []).map((opt) => opt.propertyKey)
  );

  return new Set(
    filteringProps.filteringProperties
      .filter((fp) => !keysWithOptions.has(fp.key) && !hasCustomMatcher(fp))
      .map((fp) => fp.key)
  );
};

export type ConditionSchemaContext = {
  filteringProps: ReturnType<typeof getConditionalPropertyFilterProps>;
  freeTextKeys: Set<string>;
};

/**
 * Computes the static, schema-derived filtering context for a form.
 * This result is stable for the lifetime of a form (it depends on formId,
 * schema, and feature flags — not on current field values). Callers that
 * evaluate conditions on every keystroke should memoize this separately.
 */
export const buildConditionSchemaContext = (params: {
  formId: FormId;
  customAttributeSchema: CustomisableFormDataContextState['customAttributeSchema'];
  helpers: Helpers;
  enabledFeatures: OrgFeature[];
  modules: ModuleConfig;
  additionalConditionFields?: AdditionalConditionField[];
}): ConditionSchemaContext => {
  const {
    formId,
    customAttributeSchema,
    helpers,
    enabledFeatures,
    modules,
    additionalConditionFields,
  } = params;
  const rawFilteringProps = getConditionalPropertyFilterProps({
    formId,
    schema: customAttributeSchema?.Schema,
    uiSchema: customAttributeSchema?.UiSchema,
    data: { departmentTypes: [], users: [], tagTypes: [], userGroups: [] },
    enabledFeatures,
    modules,
    helpers,
    additionalFields: additionalConditionFields,
    getStandardFieldLabel: () => '',
  });
  const filteringProps = {
    ...rawFilteringProps,
    filteringProperties: rawFilteringProps.filteringProperties.map(
      withEqualityOperators
    ),
  };
  const freeTextKeys = getFreeTextPropertyKeys(filteringProps);

  return { filteringProps, freeTextKeys };
};

/**
 * Clones conditions, lowercasing and trimming token values for free-text property keys.
 * Enumerable keys (option IDs) are left byte-exact. Stored conditions are never mutated.
 */
export const normalizeConditionsForFreeText = (
  conditions: Conditions | null | undefined,
  freeTextKeys: Set<string>
): Conditions | null | undefined => {
  // fast path: no free-text keys to normalize, skip cloning
  if (!conditions || freeTextKeys.size === 0) {
    return conditions;
  }

  const normalizeValue = <T>(value: T): T => {
    // Cloudscape always writes string[] for token values; scalar-string guard is belt-and-suspenders.
    if (Array.isArray(value)) {
      return value.map((v) =>
        typeof v === 'string' ? v.toLowerCase().trim() : v
      ) as T;
    }

    return (
      typeof value === 'string' ? value.toLowerCase().trim() : value
    ) as T;
  };

  return {
    ...conditions,
    tokenGroups: conditions.tokenGroups.map((tokenOrGroup) => {
      if ('tokens' in tokenOrGroup) {
        return {
          ...tokenOrGroup,
          tokens: tokenOrGroup.tokens.map((token) =>
            freeTextKeys.has(token.propertyKey)
              ? { ...token, value: normalizeValue(token.value) }
              : token
          ),
        };
      }

      return freeTextKeys.has(tokenOrGroup.propertyKey)
        ? { ...tokenOrGroup, value: normalizeValue(tokenOrGroup.value) }
        : tokenOrGroup;
    }),
  };
};

/**
 * Builds the runtime evaluation context: a flattened copy of the current values
 * where `CustomAttributeData.*` paths are hoisted to top-level keys, and
 * free-text string values are lowercased + trimmed.
 *
 * Accepts an optional pre-computed `schemaContext` (from `buildConditionSchemaContext`)
 * so callers that run on every keystroke can memoize the static schema derivation.
 */
const buildConditionEvalContext = (params: {
  formId: FormId;
  customAttributeSchema: CustomisableFormDataContextState['customAttributeSchema'];
  currentValues: FieldValues;
  helpers: Helpers;
  enabledFeatures: OrgFeature[];
  modules: ModuleConfig;
  additionalConditionFields?: AdditionalConditionField[];
  schemaContext?: ConditionSchemaContext;
}): {
  filteringProps: ReturnType<typeof getConditionalPropertyFilterProps>;
  visibleFieldValues: FieldValues;
  freeTextKeys: Set<string>;
} => {
  const {
    formId,
    customAttributeSchema,
    currentValues,
    helpers,
    enabledFeatures,
    modules,
    additionalConditionFields,
    schemaContext,
  } = params;

  const { filteringProps, freeTextKeys } =
    schemaContext ??
    buildConditionSchemaContext({
      formId,
      customAttributeSchema,
      helpers,
      enabledFeatures,
      modules,
      additionalConditionFields,
    });

  const visibleFieldValues: FieldValues = {
    ...currentValues,
    ...(!currentValues.CustomAttributeData
      ? {}
      : Object.entries(currentValues.CustomAttributeData).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [`CustomAttributeData.${key}`]: value,
          }),
          {}
        )),
  };

  for (const key of freeTextKeys) {
    if (typeof visibleFieldValues[key] === 'string') {
      visibleFieldValues[key] = (visibleFieldValues[key] as string)
        .toLowerCase()
        .trim();
    }
  }

  return { filteringProps, visibleFieldValues, freeTextKeys };
};

/**
 * Merges persisted `form_field_configuration` conditions with code-declared
 * `defaultConditions` from the form config registry. The single source of truth
 * for "effective" field conditions — used both to build the dependency graph
 * and to evaluate visibility, so the two never diverge.
 *
 * Gated on the `form_sections` flag: when disabled, returns the persisted
 * conditions verbatim so `defaultConditions` is fully inert and legacy
 * (flag-off) behaviour is untouched.
 *
 * Precedence when the flag is on:
 * - read-only + merge default → locked rules combine with any persisted
 *   customer rules (AND across tokenGroups): the synthesized default groups
 *   are concatenated ahead of the persisted groups. Locked rules apply even
 *   with zero customer rules, and customer rules apply with zero locked rules;
 * - read-only (strict-replace) default → always wins (the persisted row, if
 *   any, is left untouched in the DB — ignore-but-retain — but not read here);
 * - editable default → applies only when no persisted condition exists, so a
 *   customer override takes precedence and the default never clobbers it.
 */
export const getEffectiveFieldConditions = (
  formFieldConfigurations: ConditionalFields | null | undefined,
  formConfig: FormConfig | undefined,
  formSectionsEnabled: boolean
): ConditionalFields => {
  const persisted = formFieldConfigurations ?? [];
  if (!formSectionsEnabled || !formConfig) {
    return persisted;
  }

  const byFieldId = new Map<string, ConditionalField>();
  for (const ff of persisted) {
    byFieldId.set(ff.FieldId, ff);
  }

  for (const fieldConfig of Object.values(formConfig)) {
    const defaultCondition = fieldConfig.defaultConditions;
    if (!defaultCondition || defaultCondition.rules.length === 0) {
      continue;
    }

    const existing = byFieldId.get(fieldConfig.fieldId);
    const hasPersistedConditions =
      !!existing?.Conditions && existing.Conditions.tokenGroups.length > 0;

    const synthesized = defaultFieldConditionToConditions(defaultCondition);

    if (defaultCondition.readOnly && defaultCondition.merge) {
      // Merge mode: AND the locked default groups with the persisted customer
      // groups. Persisted conditions hold only customer rules (locked rules
      // are stripped at save), so concatenating never double-counts a default.
      byFieldId.set(fieldConfig.fieldId, {
        FieldId: fieldConfig.fieldId,
        Conditions: {
          operation: 'and',
          tokens: [],
          tokenGroups: [
            ...(synthesized?.tokenGroups ?? []),
            ...(existing?.Conditions?.tokenGroups ?? []),
          ],
        },
      });
    } else if (defaultCondition.readOnly || !hasPersistedConditions) {
      byFieldId.set(fieldConfig.fieldId, {
        FieldId: fieldConfig.fieldId,
        Conditions: synthesized,
      });
    }
  }

  return Array.from(byFieldId.values());
};

/**
 * Retrieves a set of field ids that are conditionally hidden based on the current form values and
 * the visibility of any ancestor fields.
 * @param options - Configuration options for determining hidden fields
 * @returns
 */
export const getHiddenFields = (options: {
  formId: FormId;
  customisableData: CustomisableFormDataContextState;
  fieldConditionGraph: ConditionGraph;
  currentValues: FieldValues;
  helpers: Helpers;
  enabledFeatures: OrgFeature[];
  modules: ModuleConfig;
  additionalConditionFields?: AdditionalConditionField[];
  formSectionsEnabled: boolean;
  schemaContext?: ConditionSchemaContext;
}): Set<string> => {
  const {
    formId,
    customisableData,
    fieldConditionGraph,
    currentValues,
    helpers,
    enabledFeatures,
    modules,
    additionalConditionFields,
    formSectionsEnabled,
    schemaContext,
  } = options;
  const { formFieldConfigurations, customAttributeSchema } = customisableData;
  const formConfigRegistry = getFormConfigRegistry(enabledFeatures, modules);
  const formConfig = formConfigRegistry[formId];

  const fieldsWithConditions = getEffectiveFieldConditions(
    formFieldConfigurations,
    formConfig,
    formSectionsEnabled
  ).filter((ff) => ff.Conditions && ff.Conditions.tokenGroups.length > 0);

  const { filteringProps, visibleFieldValues, freeTextKeys } =
    buildConditionEvalContext({
      formId,
      customAttributeSchema,
      currentValues,
      helpers,
      enabledFeatures,
      modules,
      additionalConditionFields,
      schemaContext,
    });

  // Store IDs of any fields that are hidden because their form sections are hidden
  const hiddenSectionFieldIds = new Set<string>();
  for (const fieldConfig of Object.values(formConfig)) {
    // When the flag is on, a field's `defaultConditions` supersedes the legacy
    // `visibilityControlledByFieldId` path (its synthesized condition is already
    // in `fieldsWithConditions`), so skip it here to avoid double-hiding. With
    // the flag off, `visibilityControlledByFieldId` remains the sole driver.
    if (formSectionsEnabled && fieldConfig.defaultConditions) {
      continue;
    }
    if (
      fieldConfig.visibilityControlledByFieldId &&
      !currentValues[fieldConfig.visibilityControlledByFieldId]
    ) {
      hiddenSectionFieldIds.add(fieldConfig.fieldId);
    }
  }

  // Only evaluate conditions for fields whose sections are visible
  for (const hiddenFieldId of Array.from(hiddenSectionFieldIds)) {
    delete visibleFieldValues[hiddenFieldId];
  }

  const directConditionIsFieldVisible: Record<string, boolean> = {};
  for (const fieldWithCondition of fieldsWithConditions) {
    const normalizedConditions = normalizeConditionsForFreeText(
      fieldWithCondition.Conditions,
      freeTextKeys
    );
    const filteredItems = processItems(
      [visibleFieldValues],
      {
        propertyFilteringQuery: normalizedConditions ?? undefined,
      },
      {
        propertyFiltering: {
          filteringProperties: filteringProps.filteringProperties,
          defaultQuery: normalizedConditions ?? undefined,
        },
      }
    );

    directConditionIsFieldVisible[fieldWithCondition.FieldId] =
      !!filteredItems.items.length;
  }

  const hiddenBySelfOrAncestors = new Set<string>();

  for (const fieldId of Object.keys(directConditionIsFieldVisible)) {
    const ancestors = getAncestorsAndSelf(fieldConditionGraph, fieldId);
    const anyAncestorsHidden = ancestors.some(
      (ancestor) => directConditionIsFieldVisible[ancestor] === false
    );
    if (anyAncestorsHidden) {
      hiddenBySelfOrAncestors.add(fieldId);
    }
  }

  return hiddenBySelfOrAncestors;
};

/**
 * Builds a unified condition graph including both fields and sections.
 * Sections can only depend on fields, not other sections.
 */
export const buildConditionGraph = (
  fields: ConditionalField[],
  sections: ConditionalSection[]
): ConditionGraph => {
  const graph = buildFieldConditionGraph(fields);

  // Add section nodes and their field dependencies
  sections.forEach((section) => {
    if (section.Conditions && section.Conditions.tokenGroups.length > 0) {
      graph.addVertices({ id: section.SectionId, adjacentTo: [], body: {} });

      section.Conditions.tokenGroups.forEach((tokenGroup) => {
        if ('tokens' in tokenGroup) {
          tokenGroup.tokens.forEach((token) => {
            if (token.propertyKey) {
              graph.addEdge({ from: token.propertyKey, to: section.SectionId });
            }
          });
        } else if (tokenGroup.propertyKey) {
          graph.addEdge({
            from: tokenGroup.propertyKey,
            to: section.SectionId,
          });
        }
      });
    }
  });

  return graph;
};

/**
 * Retrieves a set of section ids that are conditionally hidden based on the current form values.
 * Sections can only have conditions based on fields, not other sections.
 */
export const getHiddenSections = (options: {
  formId: FormId;
  customisableData: CustomisableFormDataContextState;
  currentValues: FieldValues;
  helpers: Helpers;
  enabledFeatures: OrgFeature[];
  modules: ModuleConfig;
  additionalConditionFields?: AdditionalConditionField[];
  schemaContext?: ConditionSchemaContext;
}): Set<string> => {
  const {
    formId,
    customisableData,
    currentValues,
    helpers,
    enabledFeatures,
    modules,
    additionalConditionFields,
    schemaContext,
  } = options;
  const { formSections, customAttributeSchema } = customisableData;

  const sectionsWithConditions =
    formSections?.filter(
      (section) =>
        section.Conditions && section.Conditions.tokenGroups.length > 0
    ) ?? [];

  if (sectionsWithConditions.length === 0) {
    return new Set<string>();
  }

  const { filteringProps, visibleFieldValues, freeTextKeys } =
    buildConditionEvalContext({
      formId,
      customAttributeSchema,
      currentValues,
      helpers,
      enabledFeatures,
      modules,
      additionalConditionFields,
      schemaContext,
    });

  const hiddenSections = new Set<string>();

  for (const section of sectionsWithConditions) {
    const normalizedConditions = normalizeConditionsForFreeText(
      section.Conditions,
      freeTextKeys
    );
    const filteredItems = processItems(
      [visibleFieldValues],
      {
        propertyFilteringQuery: normalizedConditions ?? undefined,
      },
      {
        propertyFiltering: {
          filteringProperties: filteringProps.filteringProperties,
          defaultQuery: normalizedConditions ?? undefined,
        },
      }
    );

    const isVisible = !!filteredItems.items.length;
    if (!isVisible) {
      hiddenSections.add(section.SectionId);
    }
  }

  return hiddenSections;
};
